import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, Validators } from '@angular/forms';

import { SocialSigninService } from '../../social-signin/social-signin.service';
import { describeOtherProviders } from '../../social-signin/provider-mismatch';

@Component({
    standalone: false,
    selector: 'abs-reset-password-request',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.LOGIN.RESET_PASSWORD.TITLE' | translate">
            <ng-container *ngIf="!submitted; else confirmationMessage">
                <form [formGroup]="emailForm" (ngSubmit)="submit()" class="content">
                    <div class="form-row">
                        <ion-label>{{ 'GLOBAL.LOGIN.EMAIL' | translate }}</ion-label>
                        <ion-input type="email" formControlName="email"></ion-input>
                    </div>
                    <div class="buttons">
                        <ion-button type="submit" [disabled]="emailForm.invalid || submitting">
                            {{ 'GLOBAL.LOGIN.RESET_PASSWORD.SUBMIT' | translate }}
                        </ion-button>
                    </div>
                </form>
            </ng-container>
            <ng-template #confirmationMessage>
                <p class="content" *ngIf="providerHint; else checkEmail">
                    {{ 'GLOBAL.LOGIN.PROVIDER_MISMATCH.RESET' | translate: { providers: providerHint } }}
                </p>
                <ng-template #checkEmail>
                    <p class="content">{{ 'GLOBAL.LOGIN.RESET_PASSWORD.CHECK_EMAIL' | translate }}</p>
                </ng-template>
            </ng-template>
        </abs-page-layout>
    `
})
export class ResetPasswordRequestPage implements OnInit {
    submitting = false;
    submitted = false;

    // The provider name(s) the address turned out to be registered with, shown in place of the
    // "check your email" confirmation — for a social-only account no email is coming, which is
    // exactly the dead end this page used to leave users in. Null whenever we learned nothing.
    providerHint: string = null;

    emailForm = this.fb.group({
        email: this.fb.control('', [Validators.required, Validators.email])
    });

    constructor(
        private fb: UntypedFormBuilder,
        private socialSignin: SocialSigninService,
        private route: ActivatedRoute
    ) {}

    ngOnInit() {
        // route.queryParams, not route.snapshot.queryParams: components hosted by an
        // ion-router-outlet get Ionic's ActivatedRoute proxy, whose `snapshot` is only
        // reassigned in IonRouterOutlet.activateWith() and therefore freezes at construction.
        // The proxied observables stay live. Same defect that broke the settings shell (52bf5375).
        this.route.queryParams.subscribe((params) => {
            if (params['email']) {
                this.emailForm.patchValue({ email: params['email'] });
            }
        });
    }

    async submit() {
        if (this.emailForm.invalid) {
            return;
        }

        const email = this.emailForm.value.email;
        this.submitting = true;

        try {
            await this.socialSignin.requestPasswordReset(email);
        } catch {
            // Deliberately swallowed: an unrecognised address still gets the same
            // "check your email" message, so this form cannot be used to enumerate accounts.
        }

        // Deliberately after the reset request, never before: the lookup is advisory, and
        // ordering it this way means it cannot fail in a way that stops the email being sent.
        // A null hint — which is what every failure, including the endpoint's rate limit,
        // produces — leaves the usual confirmation message in place.
        this.providerHint = describeOtherProviders(await this.socialSignin.getSignInProviders(email));

        this.submitting = false;
        this.submitted = true;
    }
}
